import { useEffect, useRef, useState, type ReactNode } from "react"
import type { ProjectFormFieldFileValue } from "@/schema/project"
import type { HouseholdRecordSource, ProjectMilestoneFormFieldType } from "@/schema/project-template"
import { DateInput, NumberInput, TextareaInput, TextInput } from "@ui/forms"
import { CheckboxInput } from "@ui/forms/inputs/checkbox-input"
import { SelectInput } from "@ui/forms/inputs/select-input"
import { Button, Input, Label } from "@ui/shad"
import { Loader2, Paperclip, Upload, X } from "lucide-react"
import { toast } from "sonner"

import { useFetchCustomFields } from "@/hooks/api/custom_fields"
import { useFetchHousehold } from "@/hooks/api/households/queries"
import { useHouseholdRecordOptions } from "@/hooks/api/projects/queries"
import { useApiContext } from "@/hooks/api/use-api-context"
import { formatCurrencyInput, parseCurrencyChars, parseCurrencyInput } from "@/components/custom-fields/number-format"

const FILE_ATTACHMENT_MAX_SIZE = 20 * 1024 * 1024
const FILE_ATTACHMENT_ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "image/png",
  "image/jpeg",
  "image/gif",
]

export interface MilestoneFormField {
  id: string
  label: string
  field_type: ProjectMilestoneFormFieldType
  required: boolean
  placeholder?: string | null
  select_options: string[] | null
  custom_field_id?: string | null
  household_record_source?: HouseholdRecordSource | null
  current_value?: string | number | boolean | ProjectFormFieldFileValue | null
  current_file?: ProjectFormFieldFileValue | null
}

export interface HouseholdRecordScope {
  householdId?: string | null
  bookId?: string | null
}

export interface FileAttachmentScope {
  householdId: string | null
  projectTemplateId: string | null
  hasStorageFolder: boolean
}

interface MilestoneFormFieldsInputProps {
  fields: MilestoneFormField[]
  values: Record<string, string | number | boolean | null>
  onChange: (values: Record<string, string | number | boolean | null>) => void
  disabled?: boolean
  householdRecordScope?: HouseholdRecordScope
  fileAttachmentScope?: FileAttachmentScope
}

export function MilestoneFormFieldsInput({
  fields,
  values,
  onChange,
  disabled,
  householdRecordScope,
  fileAttachmentScope,
}: MilestoneFormFieldsInputProps) {
  const hasLinkedFields = fields.some((field) => field.custom_field_id)
  const householdId = householdRecordScope?.householdId ?? ""
  const { data: household } = useFetchHousehold(householdId, {
    enabled: hasLinkedFields && !!householdId,
    bookIdOverride: householdRecordScope?.bookId ?? undefined,
  })
  const { data: customFields } = useFetchCustomFields("Household", { enabled: hasLinkedFields && !!householdId })

  useEffect(() => {
    if (!household || !customFields) return

    const defaults: Record<string, string | number | boolean> = {}
    for (const field of fields) {
      if (!field.custom_field_id || Object.hasOwn(values, field.id)) continue

      const customField = customFields.custom_fields.find((candidate) => candidate.id === field.custom_field_id)
      if (!customField) continue

      const value = (household as typeof household & Record<string, unknown>)[customField.field_key]
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        defaults[field.id] = value
      }
    }

    if (Object.keys(defaults).length > 0) onChange({ ...values, ...defaults })
  }, [customFields, fields, household, onChange, values])

  const updateValue = (fieldId: string, value: string | number | boolean | null) => {
    onChange({ ...values, [fieldId]: value })
  }

  return (
    <div className="space-y-3">
      {fields.map((field) => {
        const value = values[field.id]

        if (field.field_type === "boolean") {
          return (
            <CheckboxInput
              key={field.id}
              id={`form-field-${field.id}`}
              checked={value === true || value === "true"}
              onChange={(checked) => updateValue(field.id, checked)}
              label={field.label + (field.required ? " *" : "")}
              disabled={disabled}
            />
          )
        }

        return (
          <div key={field.id} className="space-y-1">
            <Label htmlFor={`form-field-${field.id}`} className="text-sm font-normal">
              {field.label}
              {field.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            {renderFieldInput(
              field,
              value,
              (v) => updateValue(field.id, v),
              disabled,
              householdRecordScope,
              fileAttachmentScope
            )}
          </div>
        )
      })}
    </div>
  )
}

function renderFieldInput(
  field: MilestoneFormField,
  value: string | number | boolean | null | undefined,
  onChange: (value: string | number | boolean | null) => void,
  disabled?: boolean,
  householdRecordScope?: HouseholdRecordScope,
  fileAttachmentScope?: FileAttachmentScope
) {
  switch (field.field_type) {
    case "text":
      return (
        <TextInput
          id={`form-field-${field.id}`}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
        />
      )
    case "textarea":
      return (
        <TextareaInput
          id={`form-field-${field.id}`}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          rows={3}
          disabled={disabled}
        />
      )
    case "number":
      return (
        <NumberInput
          id={`form-field-${field.id}`}
          value={
            typeof value === "number"
              ? value
              : value === null ||
                  value === undefined ||
                  value === "" ||
                  typeof value === "boolean" ||
                  Number.isNaN(Number(value))
                ? null
                : Number(value)
          }
          onChange={(val: number | null) => onChange(val)}
          placeholder={field.placeholder ?? undefined}
          disabled={disabled}
        />
      )
    case "date":
      return <DateFieldInput value={value} onChange={onChange} disabled={disabled} field={field} />
    case "select":
      return (
        <SelectInput
          id={`form-field-${field.id}`}
          value={(value as string) ?? null}
          onChange={(v: string | null) => onChange(v)}
          options={(field.select_options ?? []).map((opt) => ({ value: opt, label: opt }))}
          placeholder={field.placeholder ?? "Select..."}
          disabled={disabled}
        />
      )
    case "household_record":
      return (
        <HouseholdRecordFieldInput
          id={`form-field-${field.id}`}
          fieldId={field.id}
          value={(value as string) ?? null}
          onChange={(v: string | null) => onChange(v)}
          placeholder={field.placeholder ?? "Select..."}
          disabled={disabled}
          scope={householdRecordScope}
          source={field.household_record_source ?? null}
        />
      )
    case "currency":
      return (
        <CurrencyFieldInput
          id={`form-field-${field.id}`}
          value={value}
          onChange={onChange}
          placeholder={field.placeholder ?? "0.00"}
          disabled={disabled}
        />
      )
    case "boolean":
      return (
        <CheckboxInput
          id={`form-field-${field.id}`}
          checked={value === true || value === "true"}
          onChange={(checked) => onChange(checked)}
          label=""
          disabled={disabled}
        />
      )
    case "file_attachment":
      return (
        <FileAttachmentFieldInput
          id={`form-field-${field.id}`}
          value={(value as string) ?? null}
          onChange={(v: string | null) => onChange(v)}
          disabled={disabled}
          currentFile={field.current_file ?? null}
          scope={fileAttachmentScope}
        />
      )
  }
}

function DateFieldInput({
  value,
  onChange,
  disabled,
  field,
}: {
  value: string | number | boolean | null | undefined
  onChange: (value: string | number | boolean | null) => void
  disabled?: boolean
  field: MilestoneFormField
}) {
  const parseLocalDate = (dateStr: string): Date | undefined => {
    if (!dateStr) return undefined
    const [year, month, day] = dateStr.split("-").map(Number)
    if (!year || !month || !day) return undefined
    return new Date(year, month - 1, day)
  }

  const formatDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  return (
    <DateInput
      id={`form-field-${field.id}`}
      value={typeof value === "string" ? parseLocalDate(value) : undefined}
      onChange={(date) => onChange(date ? formatDate(date) : null)}
      placeholder={field.placeholder ?? undefined}
      disabled={disabled}
    />
  )
}

function HouseholdRecordFieldInput({
  id,
  fieldId,
  value,
  onChange,
  placeholder,
  disabled,
  scope,
  source,
}: {
  id: string
  fieldId: string
  value: string | null
  onChange: (value: string | null) => void
  placeholder: string
  disabled?: boolean
  scope?: HouseholdRecordScope
  source: HouseholdRecordSource | null
}) {
  const householdId = scope?.householdId ?? null
  const isPreview = !scope
  const { data, isLoading } = useHouseholdRecordOptions(householdId, fieldId, !!householdId, scope?.bookId ?? undefined)

  // Clear any value the current household's options can't represent. Covers both
  // mount-time prefill of historical answers (where source/household drift means
  // the sqid isn't in current options) and active household swaps. Backend persists
  // unchanged values via a round-trip guard, so historical DB rows survive.
  useEffect(() => {
    if (!value) return
    if (!householdId && !isPreview) {
      onChange(null)
      return
    }
    if (!isLoading && data && !data.options.some((o) => o.id === value)) {
      onChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId, isLoading, data, value, isPreview])

  // In preview mode (no scope), show dummy options based on the source type
  if (isPreview) {
    const dummyOptions = getDummyHouseholdRecordOptions(source)
    return (
      <SelectInput
        id={id}
        value={value}
        onChange={(v: string | null) => onChange(v)}
        options={dummyOptions}
        placeholder={placeholder}
        disabled={disabled}
      />
    )
  }

  const options = (data?.options ?? []).map((o) => ({ value: o.id, label: o.label }))
  const valueBelongsToHousehold = !value || options.some((o) => o.value === value)

  if (!householdId) {
    return <DisabledHouseholdRecordPlaceholder id={id}>Select a household first</DisabledHouseholdRecordPlaceholder>
  }

  if (!isLoading && options.length === 0) {
    return <DisabledHouseholdRecordPlaceholder id={id}>No records available</DisabledHouseholdRecordPlaceholder>
  }

  return (
    <SelectInput
      id={id}
      value={valueBelongsToHousehold ? value : null}
      onChange={(v: string | null) => onChange(v)}
      options={options}
      placeholder={isLoading ? "Loading..." : placeholder}
      disabled={disabled || isLoading}
    />
  )
}

function getDummyHouseholdRecordOptions(source: HouseholdRecordSource | null) {
  switch (source) {
    case "person":
      return [
        { value: "1", label: "John Smith" },
        { value: "2", label: "Jane Smith" },
      ]
    case "custodian_account":
    case "reporting_account":
      return [
        { value: "1", label: "Schwab — Joint Brokerage • 1234564821" },
        { value: "2", label: "Fidelity — Traditional IRA • 9876507392" },
      ]
    case "trust":
      return [
        { value: "1", label: "Smith Family Trust" },
        { value: "2", label: "Smith Revocable Living Trust" },
      ]
    case "address":
      return [
        { value: "1", label: "123 Main St, Denver, CO 80202" },
        { value: "2", label: "456 Oak Ave, Boulder, CO 80301" },
      ]
    default:
      return [
        { value: "1", label: "Option 1" },
        { value: "2", label: "Option 2" },
      ]
  }
}

function DisabledHouseholdRecordPlaceholder({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div
      id={id}
      className="border-input bg-muted text-muted-foreground flex h-9 w-full cursor-not-allowed items-center rounded-md border px-3 py-1 text-sm"
      aria-disabled
    >
      {children}
    </div>
  )
}

function CurrencyFieldInput({
  id,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  id: string
  value: string | number | boolean | null | undefined
  onChange: (value: string | number | boolean | null) => void
  placeholder: string
  disabled?: boolean
}) {
  const [displayValue, setDisplayValue] = useState(() => formatCurrencyInput(value as string | number))

  useEffect(() => {
    setDisplayValue(formatCurrencyInput(value as string | number))
  }, [value])

  return (
    <Input
      id={id}
      value={displayValue}
      onChange={(e) => setDisplayValue(parseCurrencyChars(e.target.value))}
      onBlur={() => {
        const parsed = parseCurrencyInput(displayValue)
        onChange(parsed)
        setDisplayValue(formatCurrencyInput(parsed))
      }}
      placeholder={placeholder}
      disabled={disabled}
    />
  )
}

function FileAttachmentFieldInput({
  id,
  value,
  onChange,
  disabled,
  currentFile,
  scope,
}: {
  id: string
  value: string | null
  onChange: (value: string | null) => void
  disabled?: boolean
  currentFile: ProjectFormFieldFileValue | null
  scope?: FileAttachmentScope
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const getContext = useApiContext()
  const [uploading, setUploading] = useState(false)
  const [stagedFile, setStagedFile] = useState<{ storage_file_id: string; file_name: string; web_url?: string } | null>(
    null
  )

  const attachedFile = (() => {
    if (stagedFile && stagedFile.storage_file_id === value) return stagedFile
    if (currentFile && currentFile.storage_file_id === value) {
      return {
        storage_file_id: currentFile.storage_file_id,
        file_name: currentFile.file_name,
        web_url: currentFile.download_url,
      }
    }
    return null
  })()

  const householdId = scope?.householdId ?? null
  const projectTemplateId = scope?.projectTemplateId ?? null
  const hasStorageFolder = scope?.hasStorageFolder ?? false

  const isReadyForUpload = Boolean(householdId) && Boolean(projectTemplateId) && hasStorageFolder
  const unavailableMessage = !householdId
    ? "Available only on projects with a household."
    : !hasStorageFolder
      ? "Link a storage folder to this household first."
      : null

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !householdId || !projectTemplateId) return

    if (file.size > FILE_ATTACHMENT_MAX_SIZE) {
      toast.error(`${file.name} is too large. Maximum size is 20MB.`)
      return
    }
    if (!FILE_ATTACHMENT_ALLOWED_TYPES.includes(file.type)) {
      toast.error(`${file.name} has an unsupported file type.`)
      return
    }

    setUploading(true)
    try {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      const { uploadFormFieldFile } = await import("@/hooks/api/projects/api")
      const result = await uploadFormFieldFile(context, householdId, projectTemplateId, file)
      setStagedFile({
        storage_file_id: result.storage_file_id,
        file_name: result.file_name,
        web_url: result.web_url,
      })
      onChange(result.storage_file_id)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed"
      toast.error(message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const handleRemove = () => {
    setStagedFile(null)
    onChange(null)
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={fileInputRef}
        id={id}
        type="file"
        accept={FILE_ATTACHMENT_ALLOWED_TYPES.join(",")}
        onChange={handleFileSelect}
        disabled={disabled || uploading || !isReadyForUpload}
        className="hidden"
      />
      {attachedFile ? (
        <div className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
          <Paperclip className="size-3.5" />
          {attachedFile.web_url ? (
            <a
              href={attachedFile.web_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 truncate hover:underline"
            >
              {attachedFile.file_name}
            </a>
          ) : (
            <span className="flex-1 truncate">{attachedFile.file_name}</span>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleRemove}
              className="text-icon hover:text-destructive rounded-sm"
              aria-label="Remove file"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading || !isReadyForUpload}
        >
          {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
          <span>{uploading ? "Uploading..." : "Upload file"}</span>
        </Button>
      )}
      {unavailableMessage && <p className="text-muted-foreground text-xs">{unavailableMessage}</p>}
    </div>
  )
}
